import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, Send, Ban, Eye, CheckCircle, Clock, FileText,
  Search, ChevronLeft, ChevronRight, FolderOpen, Filter
} from '../../icons/wp-icons';
import { getAllBestellungen, getBestellVorschau, sendeBestellung, storniereBestellung, updateLieferung } from '../../api/bestellungApi';
import Modal from '../Common/Modal';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  ENTWURF: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700', icon: FileText },
  VERSENDET: { label: 'Versendet', color: 'bg-blue-100 text-blue-700', icon: Send },
  BESTAETIGT: { label: 'Bestätigt', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  TEILGELIEFERT: { label: 'Teilgeliefert', color: 'bg-yellow-100 text-yellow-700', icon: Truck },
  VOLLSTAENDIG_GELIEFERT: { label: 'Vollständig', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  STORNIERT: { label: 'Storniert', color: 'bg-red-100 text-red-700', icon: Ban }
};

export default function AlleBestellungen() {
  const navigate = useNavigate();
  const [bestellungen, setBestellungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showVorschau, setShowVorschau] = useState(null);
  const [showLieferung, setShowLieferung] = useState(null);
  const itemsPerPage = 20;

  useEffect(() => {
    loadBestellungen();
  }, [statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const loadBestellungen = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await getAllBestellungen(params);
      setBestellungen(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleStornieren = async (id) => {
    if (!confirm('Bestellung wirklich stornieren?')) return;
    try {
      await storniereBestellung(id);
      toast.success('Bestellung storniert');
      loadBestellungen();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Stornieren');
    }
  };

  const filtered = bestellungen.filter(b => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      (b.referenzNummer || '').toLowerCase().includes(term) ||
      b.lieferant?.name?.toLowerCase().includes(term) ||
      b.projekt?.titel?.toLowerCase().includes(term) ||
      b.projekt?.nummer?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  // Statistiken
  const stats = {
    gesamt: bestellungen.length,
    offen: bestellungen.filter(b => ['ENTWURF', 'VERSENDET', 'BESTAETIGT'].includes(b.status)).length,
    inLieferung: bestellungen.filter(b => b.status === 'TEILGELIEFERT').length,
    geliefert: bestellungen.filter(b => b.status === 'VOLLSTAENDIG_GELIEFERT').length,
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistik-Karten */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-surface rounded-card shadow-card p-4">
          <div className="text-2xl font-bold">{stats.gesamt}</div>
          <div className="text-sm text-text-muted">Gesamt</div>
        </div>
        <div className="bg-surface rounded-card shadow-card p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.offen}</div>
          <div className="text-sm text-text-muted">Offen</div>
        </div>
        <div className="bg-surface rounded-card shadow-card p-4">
          <div className="text-2xl font-bold text-yellow-600">{stats.inLieferung}</div>
          <div className="text-sm text-text-muted">In Lieferung</div>
        </div>
        <div className="bg-surface rounded-card shadow-card p-4">
          <div className="text-2xl font-bold text-green-600">{stats.geliefert}</div>
          <div className="text-sm text-text-muted">Geliefert</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suchen nach Referenz, Lieferant, Projekt..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
        >
          <option value="">Alle Status</option>
          {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Bestellungen Liste */}
      {filtered.length === 0 ? (
        <div className="bg-surface rounded-card shadow-card p-8 text-center">
          <Package className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">
            {searchTerm || statusFilter ? 'Keine Bestellungen gefunden.' : 'Noch keine Bestellungen vorhanden.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-surface rounded-card shadow-card overflow-hidden">
            {/* Desktop Header */}
            <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-medium text-text-muted uppercase tracking-wide border-b border-border">
              <div className="col-span-2">Referenz</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Lieferant</div>
              <div className="col-span-3">Projekt</div>
              <div className="col-span-1 text-right">Netto</div>
              <div className="col-span-1 text-right">Aktionen</div>
            </div>

            <div className="divide-y divide-border">
              {paginated.map((b) => {
                const conf = STATUS_CONFIG[b.status] || STATUS_CONFIG.ENTWURF;
                const StatusIcon = conf.icon;
                const gesamtNetto = b.positionen?.reduce((sum, p) =>
                  sum + (p.einzelpreisNetto != null ? p.einzelpreisNetto * p.menge : 0), 0) || 0;

                return (
                  <div key={b.id} className="p-4 hover:bg-gray-50 transition">
                    {/* Desktop */}
                    <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-2">
                        <span className="font-medium text-sm">{b.referenzNummer || 'Entwurf'}</span>
                        <div className="text-xs text-text-muted">
                          {new Date(b.erstelltAm).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${conf.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {conf.label}
                        </span>
                      </div>
                      <div className="col-span-3 text-sm truncate">{b.lieferant?.name}</div>
                      <div className="col-span-3 min-w-0">
                        <button
                          onClick={() => navigate(`/projekte/${b.projekt?.id}`)}
                          className="text-sm text-primary hover:underline truncate block"
                        >
                          {b.projekt?.nummer} – {b.projekt?.titel}
                        </button>
                      </div>
                      <div className="col-span-1 text-sm text-right">
                        {gesamtNetto > 0 ? `${(gesamtNetto / 100).toFixed(2)} €` : '–'}
                      </div>
                      <div className="col-span-1 flex justify-end gap-1">
                        {b.status === 'ENTWURF' && (
                          <button
                            onClick={() => setShowVorschau(b)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Vorschau & Senden"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {['VERSENDET', 'BESTAETIGT', 'TEILGELIEFERT'].includes(b.status) && (
                          <button
                            onClick={() => setShowLieferung(b)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
                            title="Lieferung erfassen"
                          >
                            <Truck className="w-4 h-4" />
                          </button>
                        )}
                        {b.status !== 'STORNIERT' && b.status !== 'VOLLSTAENDIG_GELIEFERT' && (
                          <button
                            onClick={() => handleStornieren(b.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Stornieren"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="sm:hidden">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-medium text-sm">{b.referenzNummer || 'Entwurf'}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${conf.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {conf.label}
                        </span>
                      </div>
                      <div className="text-sm text-text-muted">{b.lieferant?.name}</div>
                      <button
                        onClick={() => navigate(`/projekte/${b.projekt?.id}`)}
                        className="text-sm text-primary hover:underline mt-0.5 block"
                      >
                        {b.projekt?.nummer} – {b.projekt?.titel}
                      </button>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-text-muted">
                          {new Date(b.erstelltAm).toLocaleDateString('de-DE')}
                          {gesamtNetto > 0 && ` • ${(gesamtNetto / 100).toFixed(2)} €`}
                        </span>
                        <div className="flex gap-1">
                          {b.status === 'ENTWURF' && (
                            <button onClick={() => setShowVorschau(b)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {['VERSENDET', 'BESTAETIGT', 'TEILGELIEFERT'].includes(b.status) && (
                            <button onClick={() => setShowLieferung(b)}
                              className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition">
                              <Truck className="w-4 h-4" />
                            </button>
                          )}
                          {b.status !== 'STORNIERT' && b.status !== 'VOLLSTAENDIG_GELIEFERT' && (
                            <button onClick={() => handleStornieren(b.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition">
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-surface rounded-card shadow-card px-4 sm:px-6 py-4">
              <div className="text-sm text-text-muted">
                Zeige {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filtered.length)} von {filtered.length} Bestellungen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Zurück</span>
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                    .map((page, idx, arr) => (
                      <span key={page} className="flex items-center">
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-1 sm:px-2 text-text-muted">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-2 sm:px-3 py-2 text-sm rounded-button transition ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-50 border border-border'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <span className="hidden sm:inline">Weiter</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vorschau & Senden Modal */}
      {showVorschau && (
        <VorschauModal
          bestellung={showVorschau}
          onClose={() => setShowVorschau(null)}
          onSent={() => { setShowVorschau(null); loadBestellungen(); }}
        />
      )}

      {/* Lieferung erfassen Modal */}
      {showLieferung && (
        <LieferungModal
          bestellung={showLieferung}
          onClose={() => setShowLieferung(null)}
          onUpdated={() => { setShowLieferung(null); loadBestellungen(); }}
        />
      )}
    </div>
  );
}

// Vorschau Modal (reused pattern from ProjektBestellungen)
function VorschauModal({ bestellung, onClose, onSent }) {
  const [vorschau, setVorschau] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getBestellVorschau(bestellung.id)
      .then(data => { setVorschau(data); })
      .catch(() => { toast.error('Fehler beim Laden der Vorschau'); onClose(); })
      .finally(() => setLoading(false));
  }, []);

  const handleSenden = async () => {
    setSending(true);
    try {
      await sendeBestellung(bestellung.id);
      toast.success('Bestellung versendet!');
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Versenden');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Bestellmail-Vorschau" size="lg">
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : vorschau && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="text-sm"><span className="font-medium text-text-muted">An:</span> {vorschau.empfaenger}</div>
            <div className="text-sm"><span className="font-medium text-text-muted">Betreff:</span> {vorschau.betreff}</div>
          </div>
          <div className="border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
            <div dangerouslySetInnerHTML={{ __html: vorschau.htmlBody }} />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSenden} disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50">
              <Send className="w-4 h-4" />
              {sending ? 'Wird versendet...' : 'Bestellung absenden'}
            </button>
            <button onClick={onClose}
              className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// Lieferung Modal (reused pattern from ProjektBestellungen)
function LieferungModal({ bestellung, onClose, onUpdated }) {
  const [positionen, setPositionen] = useState(bestellung.positionen || []);
  const [saving, setSaving] = useState(false);

  const handleUpdate = async (positionId, mengeGeliefert) => {
    setSaving(true);
    try {
      const result = await updateLieferung(bestellung.id, positionId, mengeGeliefert);
      setPositionen(result.positionen || positionen);
      toast.success('Lieferung aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Lieferung – ${bestellung.referenzNummer || 'Bestellung'}`}>
      <div className="space-y-3">
        <div className="text-sm text-text-muted mb-2">
          {bestellung.lieferant?.name} | {bestellung.projekt?.nummer} – {bestellung.projekt?.titel}
        </div>
        {positionen.map((pos) => {
          const fortschritt = pos.menge > 0 ? (pos.mengeGeliefert / pos.menge) * 100 : 0;
          const istVollstaendig = pos.mengeGeliefert >= pos.menge;

          return (
            <div key={pos.id} className={`p-3 rounded-lg border ${istVollstaendig ? 'border-green-200 bg-green-50' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium text-sm">{pos.bezeichnung}</span>
                  <span className="text-xs text-text-muted ml-2">Bestellt: {pos.menge} {pos.einheit}</span>
                </div>
                {istVollstaendig && <CheckCircle className="w-4 h-4 text-green-600" />}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${istVollstaendig ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(fortschritt, 100)}%` }}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={pos.mengeGeliefert} min="0" max={pos.menge * 2} step="0.01"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setPositionen(prev => prev.map(p => p.id === pos.id ? { ...p, mengeGeliefert: val } : p));
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val !== (bestellung.positionen.find(p => p.id === pos.id)?.mengeGeliefert || 0)) {
                        handleUpdate(pos.id, val);
                      }
                    }}
                    className="w-20 px-2 py-1 border border-border rounded text-sm text-center"
                    disabled={saving}
                  />
                  <span className="text-xs text-text-muted">/ {pos.menge}</span>
                </div>
              </div>
            </div>
          );
        })}
        <div className="pt-4">
          <button onClick={onUpdated}
            className="w-full bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition">
            Fertig
          </button>
        </div>
      </div>
    </Modal>
  );
}
